import type { IndustryPage } from '../industries';

const lines = [
  { item: 'Office suite, 4,000 sq ft: nightly service five nights a week (trash, restrooms, kitchen, vacuum, dust), monthly price', amount: '$1,850' },
  { item: 'Restrooms and break room detail clean, once a month', amount: '$240' },
  { item: 'Common-area carpet extraction, one visit per quarter', amount: '$380' },
  { item: 'Optional: strip and refinish the lobby VCT once a year', amount: '$650' },
];

const faq = [
  {
    q: 'What goes into a commercial cleaning proposal?',
    a: 'The areas and their approximate size, the tasks in each area and how often they happen, who supplies consumables like paper, liners and soap, the start date, the term and notice period, how prices are reviewed, how extra requests are priced, and the rules for keys, alarms and after-hours access.',
  },
  {
    q: 'Can my client read the proposal in Spanish?',
    a: 'Yes. Each client has a language setting: English, Spanish or Brazilian Portuguese. The quote the client opens, and its PDF, appear in the language set on that client. You can run Scopie itself in English or Spanish.',
  },
  {
    q: 'Does Scopie work out production rates or suggest a bid price?',
    a: 'No. Scopie does not calculate cleaning times or recommend prices. You build the bid your way, and Scopie turns it into a proposal the facility manager can sign, then visits on your schedule and invoices.',
  },
  {
    q: 'Does Scopie bill monthly contracts automatically?',
    a: 'No, not yet. There is no recurring invoicing in Scopie today, so you create each invoice for the work done and share its link.',
  },
];

const page: IndustryPage = {
  slug: 'janitorial',
  order: 14,
  group: 'Cleaning',
  path: '/for/janitorial',
  primaryKeyword: 'janitorial bidding software',
  secondaryKeywords: ['commercial cleaning proposal template', 'janitorial bid template', 'commercial cleaning quote software'],
  title: 'Janitorial Bidding Software for Signed Proposals | Scopie', // 56
  metaDescription:
    'Write commercial cleaning proposals by area, task and frequency, reuse your contract clauses, and get them signed online in English or Spanish.',
  h1: 'Commercial cleaning proposals a facility manager can sign',
  answerFirst:
    'Scopie helps janitorial companies write proposals that list each area, task and frequency as its own line, with periodic work like floor care offered as an option. Your contract clauses come from a saved library, and the client opens the proposal in English, Spanish or Brazilian Portuguese, then signs it online.',
  exampleQuote: { label: 'Sample office cleaning proposal', lines, total: '$3,120', depositNote: 'Notice period and price-review wording come from the company\'s clause library.' },
  blocks: [
    {
      type: 'text',
      id: 'forwarded',
      h2: 'The person you walked the building with rarely signs',
      lead: 'Your walk-through went well. Then the proposal went to a regional manager who never met you.',
      paragraphs: [
        'Commercial cleaning bids are read by people who were not on the walk-through: owners, boards and regional managers comparing four proposals at once. A one-page price that says "general cleaning, five nights" gives them nothing to compare. A proposal that names each area, the tasks in it and how often they happen gives them every reason to pick yours.',
        'Because each proposal is a link with your company name at the top, your contact can forward it upstairs, and the decision maker signs it on the same page.',
      ],
    },
    {
      type: 'sample-quote',
      id: 'sample',
      h2: 'A small office contract, broken down',
      label: 'Sample proposal: 4,000 sq ft office suite',
      lines,
      total: '$3,120',
      totalLabel: 'First-month total if floor care is chosen',
      note: 'Example figures. The annual floor refinish is optional; the client can tick it on the proposal page.',
    },
    {
      type: 'list',
      id: 'scope',
      h2: 'Scope questions to answer before the client asks',
      style: 'checklist',
      items: [
        { text: 'Which rooms are serviced nightly, weekly and monthly?' },
        { text: 'Who stocks paper towels, toilet tissue, liners and hand soap?' },
        { text: 'Are kitchen dishes, conference room setup or recycling sorting included?' },
        { text: 'What happens on holidays and building closure days?' },
        { text: 'Who holds keys and alarm codes, and what happens if an alarm is set off?' },
        { text: 'How does the client request one-off extras, and how are they priced?' },
      ],
    },
    {
      type: 'text',
      id: 'clauses',
      h2: 'Write the contract terms once',
      paragraphs: [
        'Term, notice period, price reviews, liability and access rules rarely change from one building to the next. Save them in your clause library and they pre-fill each new proposal, so the account you bid this week has the same protections as the one you won last year.',
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'How janitorial bids lose money after they are won',
      style: 'mistakes',
      items: [
        { title: 'Frequency left vague', text: '"Regular dusting" means daily to one manager and monthly to the next.' },
        { title: 'Consumables unassigned', text: 'Paper and soap add up quickly on a busy floor. Say who pays.' },
        { title: 'No price review clause', text: 'Wages and supplies go up. Without a review date, your margin shrinks every year.' },
        { title: 'Extras done for free', text: 'Post-event cleanups and carpet spills should be priced on request, not absorbed.' },
        { title: 'Square footage taken on trust', text: 'Measure, or say whose number you used.' },
      ],
    },
    {
      type: 'text',
      id: 'night-crews',
      h2: 'Night crews, languages and invoices',
      paragraphs: [
        'Signed accounts become visits on your schedule, with the team assigned to each building. Cleaners see which site is theirs tonight, not what the contract is worth. Many janitorial teams work in Spanish: the app runs in English or Spanish, and each client can receive their documents in their own language.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Janitorial proposal questions', items: faq },
  ],
  sections: [],
  faq,
  internalLinks: [
    { href: '/for', anchor: 'All industries', status: 'live' },
    { href: '/pricing', anchor: 'Scopie plans and pricing', status: 'live' },
    { href: '/for/cleaning', anchor: 'Scopie for cleaning businesses', status: 'live' },
    { href: '/for/carpet-cleaning', anchor: 'Scopie for carpet cleaners', status: 'live' },
    { href: '/templates/commercial-cleaning-proposal-template', anchor: 'free commercial cleaning proposal template', status: 'planned' },
  ],
  cta: {
    heading: 'Bid the next building with a proposal that travels',
    text: 'Save your areas, tasks and contract clauses in Scopie, and each new bid starts mostly written.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
  claimsToVerify: [],
};

export default page;
